import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Col, Row } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import { gqlUpdateCollectionpoint, gqlGetQuery, gqlGetNomoi, gqlGetTaxoffices } from './graphql';
import { toastSuccess, toastWarning } from 'utils/toasts';
import { useQuery } from '@apollo/client';
import { FaSave, FaTimesCircle } from 'react-icons/fa';
import { useTriggerMailService } from 'services/trigger-mail-service';

type Props = {
  show: boolean;
  onClose: () => void;
  id: number;
  initialName: string;
  initialAddressLine1: string;
  initialAddressLine2: string;
  initialPostalCode: string;
  initialAddressArea: string;
  initialAddressCity: string;
  initialAddressNomos: string;
  initialAddressDistrict?: string;
  initialContactPerson: string;
  initialPhone: string;
  initialMobile: string;
  initialFax: string;
  initialEmail: string;
  initialCompanyVat: string;
  initialCompanyTaxOffice: string;
  initialImaCode: string;
  initialRegionId: string;
  onUpdated?: () => void;
};

const lookupQueryVars = {
  limit: 1000,
  offset: 0,
  order: 'name',
};

/** Επιλογή από λίστα — διατηρεί τρέχουσα τιμή αν λείπει από τα δεδομένα */
const LookupSelect = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { name: string }[];
  placeholder: string;
  disabled?: boolean;
}) => {
  const hasCurrent = !value || options.some((o) => o.name === value);

  return (
    <Form.Group className="mt-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        as="select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">{placeholder}</option>
        {!hasCurrent && value && <option value={value}>{value}</option>}
        {options.map((o) => (
          <option key={o.name} value={o.name}>
            {o.name}
          </option>
        ))}
      </Form.Control>
    </Form.Group>
  );
};

const UpdateCollectionPointModal = ({
  show,
  onClose,
  initialName,
  initialAddressArea,
  initialAddressCity,
  initialAddressLine1,
  initialAddressLine2,
  initialAddressNomos,
  initialAddressDistrict,
  initialCompanyTaxOffice,
  initialCompanyVat,
  initialContactPerson,
  initialEmail,
  initialFax,
  initialImaCode,
  initialMobile,
  initialPhone,
  initialPostalCode,
  initialRegionId,
  id,
  onUpdated,
}: Props) => {
  const { triggerMailService } = useTriggerMailService();

  const [name, setName] = useState(initialName);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [addressArea, setAddressArea] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressNomos, setAddressNomos] = useState('');
  const [addressDistrict, setAddressDistrict] = useState(initialAddressDistrict ?? '');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [fax, setFax] = useState('');
  const [email, setEmail] = useState('');
  const [companyVat, setCompanyVat] = useState('');
  const [companyTaxOffice, setCompanyTaxOffice] = useState('');
  const [imaCode, setImaCode] = useState('');
  const [region, setRegion] = useState('');

  const [updateCollectionpoint, { loading }] = useMutation(gqlUpdateCollectionpoint);
  const handleSubmit = async () => {
    if (!name.trim()) {
      toastWarning('Το όνομα δεν μπορεί να είναι κενό');
      return;
    }

    try {
      await updateCollectionpoint({
        variables: {
          data: {
            id,
            name,
            addressLine1,
            addressLine2,
            postalCode,
            addressArea,
            addressCity,
            addressNomos,
            addressDistrict,
            contactPerson,
            phone,
            mobile,
            fax,
            email,
            companyVat,
            companyTaxOffice,
            imaCode,
            regionId: region ? parseInt(region) : undefined,
          },
        },
      });
      toastSuccess('Τα στοιχεία ενημερώθηκαν επιτυχώς');

      // Στέλνει mail για την αλλαγή των στοιχείων
      triggerMailService();

      // onUpdated?.(); // optionally trigger a refetch or UI update
      onClose();
    } catch (err: any) {
      toastWarning('Απέτυχε η ενημέρωση');
      console.error(err);
    }
  };

  const { data: regionsData, loading: regionsLoading } = useQuery(gqlGetQuery, {
    variables: lookupQueryVars,
    skip: !show,
  });

  const { data: nomoiData, loading: nomoiLoading } = useQuery(gqlGetNomoi, {
    variables: lookupQueryVars,
    skip: !show,
  });

  const { data: taxofficesData, loading: taxofficesLoading } = useQuery(gqlGetTaxoffices, {
    variables: lookupQueryVars,
    skip: !show,
  });

  const nomoiOptions = nomoiData?.getNomoi?.data ?? [];
  const taxofficeOptions = taxofficesData?.getTaxoffices?.data ?? [];

  useEffect(() => {
    if (show) {
      setName(initialName);
      setAddressLine1(initialAddressLine1);
      setAddressLine2(initialAddressLine2);
      setPostalCode(initialPostalCode);
      setAddressArea(initialAddressArea);
      setAddressCity(initialAddressCity);
      setAddressNomos(initialAddressNomos);
      setAddressDistrict(initialAddressDistrict ?? '');
      setContactPerson(initialContactPerson);
      setPhone(initialPhone);
      setMobile(initialMobile);
      setFax(initialFax);
      setEmail(initialEmail);
      setCompanyVat(initialCompanyVat);
      setCompanyTaxOffice(initialCompanyTaxOffice);
      setImaCode(initialImaCode);
      setRegion(initialRegionId);
    }
  }, [
    show,
    initialName,
    initialAddressLine1,
    initialAddressLine2,
    initialPostalCode,
    initialAddressArea,
    initialAddressCity,
    initialAddressNomos,
    initialAddressDistrict,
    initialContactPerson,
    initialPhone,
    initialMobile,
    initialFax,
    initialEmail,
    initialCompanyVat,
    initialCompanyTaxOffice,
    initialImaCode,
    initialRegionId,
  ]);

  return (
    <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Ενημέρωση Στοιχείων</Modal.Title>
      </Modal.Header>
      <Modal.Body className="cp-update-modal-body">
        <Row className="mx-0">
          <Col xs={12} md={6}>
            <Form.Group className="mt-3">
              <Form.Label>Όνομα</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Διεύθυνση</Form.Label>
              <Form.Control
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Διεύθυνση 2</Form.Label>
              <Form.Control
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>T.K.</Form.Label>
              <Form.Control value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Περιοχή</Form.Label>
              <Form.Control value={addressArea} onChange={(e) => setAddressArea(e.target.value)} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Πόλη</Form.Label>
              <Form.Control value={addressCity} onChange={(e) => setAddressCity(e.target.value)} />
            </Form.Group>
            <LookupSelect
              label="Νομός"
              value={addressNomos}
              onChange={setAddressNomos}
              options={nomoiOptions}
              placeholder="— Επιλέξτε Νομό —"
              disabled={nomoiLoading || loading}
            />
            <Form.Group className="mt-3">
              <Form.Label>Περιφέρεια</Form.Label>
              <Form.Control
                as="select"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                disabled={regionsLoading || loading}
              >
                <option value="">— Επιλέξτε Περιφέρεια —</option>
                {regionsData?.getRegions?.data?.map((r: any) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mt-3">
              <Form.Label>Υπεύθυνος</Form.Label>
              <Form.Control
                value={contactPerson}
                onChange={(e) => setContactPerson(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Τηλέφωνο</Form.Label>
              <Form.Control value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Κινητό</Form.Label>
              <Form.Control value={mobile} onChange={(e) => setMobile(e.target.value)} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Φαξ</Form.Label>
              <Form.Control value={fax} onChange={(e) => setFax(e.target.value)} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>E-mail</Form.Label>
              <Form.Control value={email} onChange={(e) => setEmail(e.target.value)} />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Α.Φ.Μ.</Form.Label>
              <Form.Control
                value={companyVat}
                onChange={(e) => setCompanyVat(e.target.value)}
                disabled={true}
              />
            </Form.Group>
            <LookupSelect
              label="Δ.Ο.Υ."
              value={companyTaxOffice}
              onChange={setCompanyTaxOffice}
              options={taxofficeOptions}
              placeholder="— Επιλέξτε Δ.Ο.Υ. —"
              disabled={taxofficesLoading || loading}
            />
            <Form.Group className="mt-3">
              <Form.Label>Α.Μ. ΗΜΑ</Form.Label>
              <Form.Control value={imaCode} onChange={(e) => setImaCode(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="success"
          disabled={loading}
          onClick={handleSubmit}
          style={{ padding: '0.2rem 2rem' }}
        >
          <FaSave className="mb-1" /> &nbsp; Αποθήκευση
        </Button>
        <Button variant="danger" onClick={onClose} style={{ padding: '0.2rem 2rem' }}>
          <FaTimesCircle className="mb-1" /> &nbsp; Ακύρωση
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateCollectionPointModal;
